import HttpFactory from "~/repository/factory";
import { ENDPOINTS } from "~/repository/endpoints";
import type {
  Assessment,
  AssessmentCreate,
  AssessmentUpdate,
  AssessmentPreviewResponse,
  AssessmentCreateFromPreviewPayload,
  AssessmentFilters,
  ScoringPolicy,
  ScoringPolicyCreate,
  ScoringPolicyUpdate,
  FlowPolicy,
  FlowPolicyCreate,
  FlowPolicyUpdate,
  Section,
  SectionCreate,
  SectionUpdate,
  SectionFilters,
  Question,
  AssessmentQuestionAsset,
  QuestionCreate,
  QuestionUpdate,
  QuestionFilters,
  AnswerOption,
  AnswerOptionCreate,
  AnswerOptionUpdate,
  Attempt,
  AttemptAudioPlaybackDownloadResponse,
  AttemptAudioPlaybackStartResponse,
  AttemptBulkResponseCreatePayload,
  AttemptBulkResponseSubmission,
  AttemptFilters,
  AttemptNextResponse,
  AttemptResponseCreatePayload,
  AttemptResponseSubmission,
  AttemptSubmitResponse,
  Response,
  ResponseFilters,
  ResponseManualCorrectionPayload,
  PaginatedResponse,
  ToggleActiveResponse,
  DuplicateAssessmentResponse,
  ResetAttemptResponse,
  InvalidateAttemptResponse,
  RecalculateAttemptResponse,
  PolicyFilters,
  GenerateAssessmentPreviewPayload,
  AssessmentContentLinks,
  AssessmentContentLinksUpdatePayload,
  AssessmentLessonLink,
  AssessmentLessonLinksUpdatePayload,
  AssessmentBulkGenerationJob,
  AssessmentBulkGenerationOptions,
  AssessmentBulkGenerationPayload,
} from "~/types/admin/assessment";
import { buildAttemptAudioPlaybackDownloadResponse } from "~/repository/modules/assessmentAudioPlayback";
import {
  buildAttemptBulkResponseSubmissionPayload,
  buildAttemptResponseSubmissionPayload,
} from "~/utils/assessmentResponseSubmission";

class AdminAssessmentModule extends HttpFactory {
  // ==========================================
  // Scoring Policies
  // ==========================================

  async getScoringPolicies(
    filters?: PolicyFilters,
  ): Promise<PaginatedResponse<ScoringPolicy>> {
    return await this.get(
      ENDPOINTS.ADMIN.ASSESSMENT.SCORING_POLICIES,
      filters || {},
    );
  }

  async getScoringPolicy(id: number): Promise<ScoringPolicy> {
    return await this.get(
      `${ENDPOINTS.ADMIN.ASSESSMENT.SCORING_POLICIES}${id}/`,
    );
  }

  async createScoringPolicy(data: ScoringPolicyCreate): Promise<ScoringPolicy> {
    return await this.post(ENDPOINTS.ADMIN.ASSESSMENT.SCORING_POLICIES, data);
  }

  async updateScoringPolicy(
    id: number,
    data: ScoringPolicyUpdate,
  ): Promise<ScoringPolicy> {
    return await this.patch(
      `${ENDPOINTS.ADMIN.ASSESSMENT.SCORING_POLICIES}${id}/`,
      data,
    );
  }

  async deleteScoringPolicy(id: number): Promise<void> {
    return await this.delete(
      `${ENDPOINTS.ADMIN.ASSESSMENT.SCORING_POLICIES}${id}/`,
    );
  }

  // ==========================================
  // Flow Policies
  // ==========================================

  async getFlowPolicies(
    filters?: PolicyFilters,
  ): Promise<PaginatedResponse<FlowPolicy>> {
    return await this.get(
      ENDPOINTS.ADMIN.ASSESSMENT.FLOW_POLICIES,
      filters || {},
    );
  }

  async getFlowPolicy(id: number): Promise<FlowPolicy> {
    return await this.get(`${ENDPOINTS.ADMIN.ASSESSMENT.FLOW_POLICIES}${id}/`);
  }

  async createFlowPolicy(data: FlowPolicyCreate): Promise<FlowPolicy> {
    return await this.post(ENDPOINTS.ADMIN.ASSESSMENT.FLOW_POLICIES, data);
  }

  async updateFlowPolicy(
    id: number,
    data: FlowPolicyUpdate,
  ): Promise<FlowPolicy> {
    return await this.patch(
      `${ENDPOINTS.ADMIN.ASSESSMENT.FLOW_POLICIES}${id}/`,
      data,
    );
  }

  async deleteFlowPolicy(id: number): Promise<void> {
    return await this.delete(
      `${ENDPOINTS.ADMIN.ASSESSMENT.FLOW_POLICIES}${id}/`,
    );
  }

  // ==========================================
  // Assessments
  // ==========================================

  async getAssessments(
    filters?: AssessmentFilters,
  ): Promise<PaginatedResponse<Assessment>> {
    return await this.get(
      ENDPOINTS.ADMIN.ASSESSMENT.ASSESSMENTS,
      filters || {},
    );
  }

  async getAssessment(id: number): Promise<Assessment> {
    return await this.get(`${ENDPOINTS.ADMIN.ASSESSMENT.ASSESSMENTS}${id}/`);
  }

  async createAssessment(data: AssessmentCreate): Promise<Assessment> {
    return await this.post(ENDPOINTS.ADMIN.ASSESSMENT.ASSESSMENTS, data);
  }

  async generateAssessmentPreview(
    data: GenerateAssessmentPreviewPayload,
  ): Promise<AssessmentPreviewResponse> {
    return await this.post(
      ENDPOINTS.ADMIN.ASSESSMENT.ASSESSMENTS_GENERATE_PREVIEW,
      data,
    );
  }

  async createAssessmentFromPreview(
    data: AssessmentCreateFromPreviewPayload,
  ): Promise<Assessment> {
    return await this.post(
      ENDPOINTS.ADMIN.ASSESSMENT.ASSESSMENTS_CREATE_FROM_PREVIEW,
      data,
    );
  }

  async getAssessmentLessonLinks(id: number): Promise<AssessmentLessonLink[]> {
    return await this.get(
      `${ENDPOINTS.ADMIN.ASSESSMENT.ASSESSMENTS}${id}/lessons/`,
    );
  }

  async updateAssessmentLessonLinks(
    id: number,
    data: AssessmentLessonLinksUpdatePayload,
  ): Promise<AssessmentLessonLink[]> {
    return await this.put(
      `${ENDPOINTS.ADMIN.ASSESSMENT.ASSESSMENTS}${id}/lessons/`,
      data,
    );
  }

  async getAssessmentContentLinks(id: number): Promise<AssessmentContentLinks> {
    return await this.get(
      `${ENDPOINTS.ADMIN.ASSESSMENT.ASSESSMENTS}${id}/content-links/`,
    );
  }

  async updateAssessmentContentLinks(
    id: number,
    data: AssessmentContentLinksUpdatePayload,
  ): Promise<AssessmentContentLinks> {
    return await this.put(
      `${ENDPOINTS.ADMIN.ASSESSMENT.ASSESSMENTS}${id}/content-links/`,
      data,
    );
  }

  async getBulkGenerationOptions(): Promise<AssessmentBulkGenerationOptions> {
    return await this.get(ENDPOINTS.ADMIN.ASSESSMENT.BULK_GENERATION_OPTIONS);
  }

  async createBulkGenerationJob(
    data: AssessmentBulkGenerationPayload,
  ): Promise<AssessmentBulkGenerationJob> {
    return await this.post(
      ENDPOINTS.ADMIN.ASSESSMENT.BULK_GENERATION_JOBS,
      data,
    );
  }

  async getBulkGenerationJob(id: number): Promise<AssessmentBulkGenerationJob> {
    return await this.get(
      `${ENDPOINTS.ADMIN.ASSESSMENT.BULK_GENERATION_JOBS}${id}/`,
    );
  }

  async retryFailedBulkGenerationJob(
    id: number,
  ): Promise<AssessmentBulkGenerationJob> {
    return await this.post(
      `${ENDPOINTS.ADMIN.ASSESSMENT.BULK_GENERATION_JOBS}${id}/retry-failed/`,
      {},
    );
  }

  async updateAssessment(
    id: number,
    data: AssessmentUpdate,
  ): Promise<Assessment> {
    return await this.patch(
      `${ENDPOINTS.ADMIN.ASSESSMENT.ASSESSMENTS}${id}/`,
      data,
    );
  }

  async deleteAssessment(id: number): Promise<void> {
    return await this.delete(`${ENDPOINTS.ADMIN.ASSESSMENT.ASSESSMENTS}${id}/`);
  }

  async toggleAssessmentActive(id: number): Promise<ToggleActiveResponse> {
    return await this.post(
      `${ENDPOINTS.ADMIN.ASSESSMENT.ASSESSMENTS}${id}/toggle_active/`,
      {},
    );
  }

  async duplicateAssessment(id: number): Promise<DuplicateAssessmentResponse> {
    return await this.post(
      `${ENDPOINTS.ADMIN.ASSESSMENT.ASSESSMENTS}${id}/duplicate/`,
      {},
    );
  }

  async simulateAssessment(id: number): Promise<Attempt> {
    return await this.post(
      `${ENDPOINTS.ADMIN.ASSESSMENT.ASSESSMENTS}${id}/simulate/`,
      {},
    );
  }

  // ==========================================
  // Sections
  // ==========================================

  async getSections(
    filters?: SectionFilters,
  ): Promise<PaginatedResponse<Section>> {
    return await this.get(ENDPOINTS.ADMIN.ASSESSMENT.SECTIONS, filters || {});
  }

  async getSection(id: number): Promise<Section> {
    return await this.get(`${ENDPOINTS.ADMIN.ASSESSMENT.SECTIONS}${id}/`);
  }

  async createSection(data: SectionCreate): Promise<Section> {
    return await this.post(ENDPOINTS.ADMIN.ASSESSMENT.SECTIONS, data);
  }

  async updateSection(id: number, data: SectionUpdate): Promise<Section> {
    return await this.patch(
      `${ENDPOINTS.ADMIN.ASSESSMENT.SECTIONS}${id}/`,
      data,
    );
  }

  async deleteSection(id: number): Promise<void> {
    return await this.delete(`${ENDPOINTS.ADMIN.ASSESSMENT.SECTIONS}${id}/`);
  }

  async uploadSectionAsset(
    sectionId: number,
    data: FormData,
  ): Promise<AssessmentQuestionAsset> {
    return await this.post(
      `${ENDPOINTS.ADMIN.ASSESSMENT.SECTIONS}${sectionId}/assets/`,
      data,
    );
  }

  // ==========================================
  // Questions
  // ==========================================

  async getQuestions(
    filters?: QuestionFilters,
  ): Promise<PaginatedResponse<Question>> {
    return await this.get(ENDPOINTS.ADMIN.ASSESSMENT.QUESTIONS, filters || {});
  }

  async getQuestion(id: number): Promise<Question> {
    return await this.get(`${ENDPOINTS.ADMIN.ASSESSMENT.QUESTIONS}${id}/`);
  }

  async createQuestion(data: QuestionCreate): Promise<Question> {
    return await this.post(ENDPOINTS.ADMIN.ASSESSMENT.QUESTIONS, data);
  }

  async updateQuestion(id: number, data: QuestionUpdate): Promise<Question> {
    return await this.patch(
      `${ENDPOINTS.ADMIN.ASSESSMENT.QUESTIONS}${id}/`,
      data,
    );
  }

  async deleteQuestion(id: number): Promise<void> {
    return await this.delete(`${ENDPOINTS.ADMIN.ASSESSMENT.QUESTIONS}${id}/`);
  }

  async duplicateQuestion(id: number): Promise<Question> {
    return await this.post(
      `${ENDPOINTS.ADMIN.ASSESSMENT.QUESTIONS}${id}/duplicate/`,
      {},
    );
  }

  async uploadQuestionAsset(
    questionId: number,
    data: FormData,
  ): Promise<AssessmentQuestionAsset> {
    return await this.post(
      `${ENDPOINTS.ADMIN.ASSESSMENT.QUESTIONS}${questionId}/assets/`,
      data,
    );
  }

  // ==========================================
  // Answer Options
  // ==========================================

  async getAnswerOptions(
    questionId?: number,
  ): Promise<PaginatedResponse<AnswerOption>> {
    return await this.get(
      ENDPOINTS.ADMIN.ASSESSMENT.ANSWER_OPTIONS,
      questionId ? { question: questionId } : {},
    );
  }

  async getAnswerOption(id: number): Promise<AnswerOption> {
    return await this.get(`${ENDPOINTS.ADMIN.ASSESSMENT.ANSWER_OPTIONS}${id}/`);
  }

  async createAnswerOption(data: AnswerOptionCreate): Promise<AnswerOption> {
    return await this.post(ENDPOINTS.ADMIN.ASSESSMENT.ANSWER_OPTIONS, data);
  }

  async updateAnswerOption(
    id: number,
    data: AnswerOptionUpdate,
  ): Promise<AnswerOption> {
    return await this.patch(
      `${ENDPOINTS.ADMIN.ASSESSMENT.ANSWER_OPTIONS}${id}/`,
      data,
    );
  }

  async deleteAnswerOption(id: number): Promise<void> {
    return await this.delete(
      `${ENDPOINTS.ADMIN.ASSESSMENT.ANSWER_OPTIONS}${id}/`,
    );
  }

  // ==========================================
  // Attempts (Visualização e Gerenciamento)
  // ==========================================

  async getAttempts(
    filters?: AttemptFilters,
  ): Promise<PaginatedResponse<Attempt>> {
    return await this.get(ENDPOINTS.ADMIN.ASSESSMENT.ATTEMPTS, filters || {});
  }

  async getAttempt(id: number): Promise<Attempt> {
    return await this.get(`${ENDPOINTS.ADMIN.ASSESSMENT.ATTEMPTS}${id}/`);
  }

  async getNextAttemptQuestion(id: number): Promise<AttemptNextResponse> {
    return await this.get(`${ENDPOINTS.ADMIN.ASSESSMENT.ATTEMPTS}${id}/next/`);
  }

  async submitAttemptResponse(
    id: number,
    data: AttemptResponseCreatePayload,
  ): Promise<AttemptResponseSubmission> {
    return await this.post(
      `${ENDPOINTS.ADMIN.ASSESSMENT.ATTEMPTS}${id}/responses/`,
      buildAttemptResponseSubmissionPayload(data),
    );
  }

  async submitAttemptResponsesBulk(
    id: number,
    data: AttemptBulkResponseCreatePayload,
  ): Promise<AttemptBulkResponseSubmission> {
    return await this.post(
      `${ENDPOINTS.ADMIN.ASSESSMENT.ATTEMPTS}${id}/responses/bulk/`,
      buildAttemptBulkResponseSubmissionPayload(data),
    );
  }

  async startAttemptAudioPlayback(
    id: number,
    assetId: number,
  ): Promise<AttemptAudioPlaybackStartResponse> {
    return await this.post(
      `${ENDPOINTS.ADMIN.ASSESSMENT.ATTEMPTS}${id}/audio-playbacks/${assetId}/start/`,
      {},
    );
  }

  async downloadAttemptAudioPlayback(
    audioUrl: string,
  ): Promise<AttemptAudioPlaybackDownloadResponse> {
    const response = await this.getResponse<Blob>(
      audioUrl,
      {},
      { responseType: "blob" },
    );
    return buildAttemptAudioPlaybackDownloadResponse(response);
  }

  async submitAttempt(id: number): Promise<AttemptSubmitResponse> {
    return await this.post(
      `${ENDPOINTS.ADMIN.ASSESSMENT.ATTEMPTS}${id}/submit/`,
      {},
    );
  }

  async recalculateAttempt(id: number): Promise<RecalculateAttemptResponse> {
    return await this.post(
      `${ENDPOINTS.ADMIN.ASSESSMENT.ATTEMPTS}${id}/recalculate/`,
      {},
    );
  }

  async releaseCorrection(id: number): Promise<Attempt> {
    return await this.post(
      `${ENDPOINTS.ADMIN.ASSESSMENT.ATTEMPTS}${id}/release-correction/`,
      {},
    );
  }

  async resetAttempt(id: number): Promise<ResetAttemptResponse> {
    return await this.post(
      `${ENDPOINTS.ADMIN.ASSESSMENT.ATTEMPTS}${id}/reset/`,
      {},
    );
  }

  async invalidateAttempt(id: number): Promise<InvalidateAttemptResponse> {
    return await this.delete(
      `${ENDPOINTS.ADMIN.ASSESSMENT.ATTEMPTS}${id}/invalidate/`,
    );
  }

  // ==========================================
  // Responses (Correção Manual)
  // ==========================================

  async getResponses(
    filters?: ResponseFilters,
  ): Promise<PaginatedResponse<Response>> {
    return await this.get(ENDPOINTS.ADMIN.ASSESSMENT.RESPONSES, filters || {});
  }

  async getAssessmentResponse(id: number): Promise<Response> {
    return await this.get(`${ENDPOINTS.ADMIN.ASSESSMENT.RESPONSES}${id}/`);
  }

  async updateResponse(
    id: number,
    data: ResponseManualCorrectionPayload,
  ): Promise<Response> {
    return await this.patch(
      `${ENDPOINTS.ADMIN.ASSESSMENT.RESPONSES}${id}/`,
      data,
    );
  }
}

export { AdminAssessmentModule };
